import { FC, useEffect, useState, useMemo } from 'react'
import cn from 'classnames'
import { useAddAlert } from '@/hooks/useAddAlert'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { CrmFunnelStatusItem } from '@/api/types/crm/crmFunnels.types'
import Select from '../../../base/Select/Select'
import { useLeadStatuses } from '../../blocks/StatusListBlock/LeadStatusContext'
import { useCRMContext } from '@pages/Admin/CRM/CRMContext'
import classes from './index.module.css'
import useFullLeadModalAccess from '../../hooks/useFullLeadModalAccess'
import { LeadWithFunnelData } from '../../blocks/MainInfoBlock/MainInfoBlock'
import { useLeadRefetch } from '../../LeadRefetchContext'
import { fetchLeadCardInFunnelDataDeduped } from '../../leadModalRefetchMerge'
import { Tooltip } from 'antd'

interface SelectNewStatusProps {
  leadId: number
  lead: LeadWithFunnelData
  funnelId: number | null
  placeholder?: string
  className?: string
  allowedStatusIds?: number[]
  funnelStatuses?: CrmFunnelStatusItem[]
  skipCurrentStatusFetch?: boolean
  isFunnelAvailable?: boolean
  onStatusChanged?: () => void
  /**
   * Если true — смена в селекте только локально; коммит статуса
   * выполняется снаружи (например по «Сохранить»).
   */
  deferCommit?: boolean
  /** Синхронизация выбранного id при `deferCommit` (в т.ч. после подгрузки с API). */
  onDeferredStatusSync?: (statusId: number | null) => void
  /** Нет сохранённого менеджера по связке — смену статуса блокируем. */
  isStatusChangeBlocked?: boolean
}

const SelectNewStatus: FC<SelectNewStatusProps> = ({
  leadId,
  funnelId,
  placeholder = 'Статус',
  className,
  allowedStatusIds,
  funnelStatuses = [],
  skipCurrentStatusFetch = false,
  isFunnelAvailable = true,
  onStatusChanged,
  deferCommit = false,
  onDeferredStatusSync,
  isStatusChangeBlocked = false,
  lead,
}) => {
  const addAlert = useAddAlert()
  const {
    preloadingData: { statuses: allStatuses },
  } = useCRMContext()
  const { statuses, loading, refreshStatuses } = useLeadStatuses()
  const access = useFullLeadModalAccess()
  const {
    resolveLeadFunnelId,
    getCardInFunnelSnapshot,
    putCardInFunnelSnapshot,
    multipleDealsEnabled,
    selectedLeadFunnelId,
    committedCurrentStatusId,
  } = useLeadRefetch()
  const [valueStatusId, setValueStatusId] = useState<number | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const sortedFunnelStatuses = useMemo(
    () =>
      [...funnelStatuses].sort(
        (a, b) =>
          (a.sorting ?? Number.MAX_SAFE_INTEGER) -
          (b.sorting ?? Number.MAX_SAFE_INTEGER),
      ),
    [funnelStatuses],
  )

  const optionsStatuses = useMemo(() => {
    if (sortedFunnelStatuses.length > 0) {
      const filteredFunnelStatuses =
        allowedStatusIds && allowedStatusIds.length > 0
          ? sortedFunnelStatuses.filter((status) =>
              allowedStatusIds.includes(status.id),
            )
          : sortedFunnelStatuses

      return filteredFunnelStatuses
    }

    const currentStatus = allStatuses.find(
      (status) => status.id === valueStatusId,
    )

    return allowedStatusIds && allowedStatusIds.length > 0
      ? allStatuses.filter((status) => allowedStatusIds.includes(status.id))
      : currentStatus
      ? [currentStatus]
      : []
  }, [allStatuses, valueStatusId, allowedStatusIds, sortedFunnelStatuses])

  /** Без сделок: status_id из lead.funnels по funnel_id (одна связка на воронку). */
  const currentFunnelStatusIdFromLead = useMemo(() => {
    if (multipleDealsEnabled || !funnelId) return null

    const raw = (
      (lead?.funnels as
        | Array<{
            id?: number | string | null
            current_status_id?: number | string | null
          }>
        | null
        | undefined) ?? []
    ).find(
      (funnel) => Number(funnel?.id) === Number(funnelId),
    )?.current_status_id

    const id = Number(raw)
    return Number.isFinite(id) && id > 0 ? id : null
  }, [lead?.funnels, funnelId, multipleDealsEnabled])

  const leadFunnelIdForFetch =
    multipleDealsEnabled &&
    selectedLeadFunnelId != null &&
    Number.isFinite(selectedLeadFunnelId) &&
    selectedLeadFunnelId > 0
      ? selectedLeadFunnelId
      : undefined

  useEffect(() => {
    if (!funnelId) {
      setValueStatusId(null)
      return
    }
    if (!isFunnelAvailable) {
      setValueStatusId(null)
      setStatusLoading(false)
      return
    }
    if (skipCurrentStatusFetch) {
      // Не сбрасываем value — иначе селект статуса мигает пустым на время refetch.
      setStatusLoading(false)
      return
    }

    // Сделки ON — статус активной сделки из card-funnel / committed.
    if (multipleDealsEnabled) {
      if (committedCurrentStatusId != null) {
        setValueStatusId(committedCurrentStatusId)
        setStatusLoading(false)
        return
      }
    } else if (currentFunnelStatusIdFromLead) {
      setValueStatusId(currentFunnelStatusIdFromLead)
      setStatusLoading(false)
      return
    }

    let cancelled = false

    const loadCurrentStatus = async () => {
      setStatusLoading(true)

      try {
        const cached = getCardInFunnelSnapshot(funnelId, leadFunnelIdForFetch)
        if (cached) {
          const currentStatus = cached.current_status ?? cached.status
          if (cancelled) return
          if (currentStatus?.id) {
            setValueStatusId(currentStatus.id)
            return
          }
        }

        const payload = await fetchLeadCardInFunnelDataDeduped(
          leadId,
          funnelId,
          leadFunnelIdForFetch,
        )
        if (payload) {
          const stampedLeadFunnelId =
            Number(payload.lead_funnel_id) > 0
              ? Number(payload.lead_funnel_id)
              : leadFunnelIdForFetch
          putCardInFunnelSnapshot(funnelId, {
            ...payload,
            ...(stampedLeadFunnelId != null
              ? { lead_funnel_id: stampedLeadFunnelId }
              : {}),
          })
        }

        const currentStatus = payload?.current_status ?? payload?.status

        if (cancelled) return
        if (currentStatus?.id) {
          setValueStatusId(currentStatus.id)
          return
        }

        // Фолбэк: если в воронке нет текущего/дефолтного статуса,
        // выбираем первый доступный по sorting.
        const fallbackStatus = sortedFunnelStatuses[0]
        setValueStatusId(fallbackStatus?.id ?? null)
      } catch {
        if (cancelled) return

        const fallbackStatus = sortedFunnelStatuses[0]
        if (fallbackStatus) {
          setValueStatusId(fallbackStatus.id)
          return
        }

        setValueStatusId(null)
      } finally {
        if (!cancelled) setStatusLoading(false)
      }
    }

    loadCurrentStatus()

    return () => {
      cancelled = true
    }
  }, [
    funnelId,
    leadId,
    sortedFunnelStatuses,
    skipCurrentStatusFetch,
    isFunnelAvailable,
    currentFunnelStatusIdFromLead,
    multipleDealsEnabled,
    committedCurrentStatusId,
    leadFunnelIdForFetch,
    getCardInFunnelSnapshot,
    putCardInFunnelSnapshot,
  ])

  useEffect(() => {
    if (!deferCommit) return
    onDeferredStatusSync?.(valueStatusId)
  }, [deferCommit, valueStatusId, onDeferredStatusSync])

  const currentLeadFunnelId = useMemo(
    () => resolveLeadFunnelId({ lead, selectedFunnelId: funnelId }),
    [lead, funnelId, resolveLeadFunnelId],
  )

  const handlerSelectStatus = async (statusId: number) => {
    try {
      if (!currentLeadFunnelId) {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось определить воронку лида',
          type: 'error',
        })
        return
      }

      if (multipleDealsEnabled) {
        if (!funnelId) {
          addAlert({
            title: 'Ошибка',
            text: 'Не удалось определить воронку лида',
            type: 'error',
          })
          return
        }

        const response = await crmApi.leads.updateLeadCardInFunnel(
          {
            lead_id: leadId,
            funnel_id: funnelId,
            lead_funnel_id: currentLeadFunnelId,
          },
          { current_status_id: statusId },
        )
        if (isError(response)) throw new Error()
      } else {
        const response = await crmApi.insertLeadStatus({
          status_id: statusId,
          lead_funnel_id: currentLeadFunnelId,
        })
        if (isError(response)) throw new Error()
      }

      addAlert({
        title: 'Успешно',
        text: `Успешно добавлено статус`,
        type: 'success',
      })

      refreshStatuses()
      onStatusChanged?.()

      if (funnelId) {
        const data = await fetchLeadCardInFunnelDataDeduped(
          leadId,
          funnelId,
          multipleDealsEnabled ? currentLeadFunnelId : undefined,
        )
        if (data) {
          putCardInFunnelSnapshot(funnelId, data)
          const currentStatus = data.current_status ?? data.status
          setValueStatusId(currentStatus?.id ?? statusId)
        }
      }
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка добавлении статуса',
        type: 'error',
      })
    }
  }

  const statusBlockedTooltipTitle = isStatusChangeBlocked
    ? 'Изменение статуса заблокировано: в заявке ещё не выбран менеджер по воронке.'
    : ''

  return (
    <>
      {statusBlockedTooltipTitle ? (
        <Tooltip title={statusBlockedTooltipTitle}>
          <span>
            <Select
              size="small"
              placeholder={placeholder}
              className={cn(classes.select, className)}
              value={valueStatusId}
              onChange={(val) => {
                setValueStatusId(val)
                if (!deferCommit) {
                  handlerSelectStatus(val)
                }
              }}
              options={optionsStatuses.map((status) => ({
                label: (
                  <div className={classes.itemStatusWrapper}>
                    <div
                      className={classes.itemStatusColor}
                      style={{ backgroundColor: status.color }}
                    />
                    <p className={classes.itemStatusName}>{status.name}</p>
                  </div>
                ),
                value: status.id,
              }))}
              loading={loading || statusLoading}
              disabled={
                !access.canChangeLeadStatus ||
                isStatusChangeBlocked ||
                !funnelId ||
                sortedFunnelStatuses.length === 0
              }
              suffixIcon={!access.canChangeLeadStatus ? null : undefined}
            />
          </span>
        </Tooltip>
      ) : (
        <Select
          size="small"
          placeholder={placeholder}
          className={cn(classes.select, className)}
          value={valueStatusId}
          onChange={(val) => {
            setValueStatusId(val)
            if (!deferCommit) {
              handlerSelectStatus(val)
            }
          }}
          options={optionsStatuses.map((status) => ({
            label: (
              <div className={classes.itemStatusWrapper}>
                <div
                  className={classes.itemStatusColor}
                  style={{ backgroundColor: status.color }}
                />
                <p className={classes.itemStatusName}>{status.name}</p>
              </div>
            ),
            value: status.id,
          }))}
          loading={loading || statusLoading}
          disabled={
            !access.canChangeLeadStatus ||
            isStatusChangeBlocked ||
            !funnelId ||
            sortedFunnelStatuses.length === 0
          }
          suffixIcon={!access.canChangeLeadStatus ? null : undefined}
        />
      )}
    </>
  )
}

export default SelectNewStatus
